import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Download, X } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  const isIos = useMemo(() => /iphone|ipad|ipod/i.test(window.navigator.userAgent), []);
  const isStandalone = useMemo(
    () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true,
    []
  );

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const showAndroidPrompt = Boolean(deferredPrompt) && !isDismissed && !isStandalone;
  const showIosHint = isIos && !isStandalone && !isDismissed;

  if (!showAndroidPrompt && !showIosHint) return null;

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    setIsInstalling(true);
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setIsDismissed(true);
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[60]" data-testid="install-prompt-container">
      <Card className="mx-auto max-w-xl bg-background/95 border border-white/10 backdrop-blur-md shadow-xl" data-testid="install-prompt-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-white mb-1" data-testid="install-prompt-title">Install BarsConnect</h3>
              {showAndroidPrompt ? (
                <p className="text-xs text-muted-foreground" data-testid="install-prompt-android-copy">
                  Add BarsConnect to your home screen for app-like access.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="install-prompt-ios-copy">
                  On iPhone: tap Share, then choose “Add to Home Screen”.
                </p>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDismissed(true)}
              data-testid="install-prompt-dismiss-btn"
              aria-label="Dismiss install prompt"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {showAndroidPrompt && (
            <div className="mt-3">
              <Button onClick={handleInstallClick} disabled={isInstalling} data-testid="install-prompt-install-btn">
                <Download className="h-4 w-4 mr-2" />
                {isInstalling ? 'Preparing...' : 'Install App'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
